import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AddressWiseReport = () => {
    const navigate = useNavigate();
    const { appId, language } = useAppId();
    const lang = language || 'en';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Address Wise Report",
            searchPlaceholder: "Search by Address...",
            noData: "No address records found.",
            loadingData: "Fetching report...",
            totalVoters: "Voters",
            social: "Social",
            facebook: "Facebook",
            instagram: "Instagram"
        },
        hi: {
            pageTitle: "पता वार रिपोर्ट",
            searchPlaceholder: "पते से खोजें...",
            noData: "कोई पता रिकॉर्ड नहीं मिला।",
            loadingData: "रिपोर्ट ला रहा है...",
            totalVoters: "मतदाता",
            social: "सोशल",
            facebook: "Facebook",
            instagram: "Instagram"
        },
        gu: {
            pageTitle: "સરનામું વાઇઝ રિપોર્ટ",
            searchPlaceholder: "સરનામાથી શોધો...",
            noData: "કોઈ સરનામું રેકોર્ડ મળ્યો નથી.",
            loadingData: "રિપોર્ટ લાવી રહ્યા છીએ...",
            totalVoters: "મતદાર",
            social: "સોશ્યલ",
            facebook: "Facebook",
            instagram: "Instagram"
        }
    };

    const txt = t[lang] || t.gu;

    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (!appId) {
            navigate('/login');
            return;
        }
        fetchAddressSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const fetchAddressSummary = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/dis_address_wise_social_media_summary`, {
                app_id: appId
            });

            if (response.data && response.data.success) {
                setReportData(response.data.data.result || []);
            } else {
                setReportData([]);
            }
        } catch (err) {
            console.error("Error fetching address-wise report:", err);
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    // Filter Logic - Search by address
    const filteredData = reportData.filter(address =>
        address.add_eng?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="report-page-container">
            {/* Header */}
            <div className="header-wrapper shadow-sm">
                <Header title={txt.pageTitle} />
            </div>

            <div className="content-container container-fluid p-3 p-md-4 mx-auto">

                {/* Search Bar */}
                <div className="search-bar-container mb-4 slide-up-element">
                    <div className="search-wrapper">
                        <svg className="search-icon" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            type="text"
                            className="search-input"
                            placeholder={txt.searchPlaceholder}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
                                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="text-center p-5 slide-up-element">
                        <svg className="spinner-svg" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" />
                        </svg>
                        <p className="mt-3 text-muted font-weight-bold">{txt.loadingData}</p>
                    </div>
                ) : filteredData.length === 0 ? (
                    <div className="text-center text-muted p-5 bg-white rounded shadow-sm border slide-up-element">
                        <div style={{ fontSize: '50px', marginBottom: '16px' }}>📍</div>
                        <h4>{txt.noData}</h4>
                    </div>
                ) : (
                    <div className="cards-grid row slide-up-element" style={{ animationDelay: '0.1s' }}>
                        {filteredData.map((address, index) => (
                            <div key={index} className="col-12 col-sm-6 col-md-6 col-lg-4 mb-4">
                                <div className="address-card">

                                    {/* Card Header (Dark Blue) - Shows address directly */}
                                    <div className="card-header-bg">
                                        <div className="header-left">
                                            <div className="header-icon-box">
                                                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                                                </svg>
                                            </div>
                                            <div className="address-title">
                                                {address.add_eng || 'N/A'}
                                            </div>
                                        </div>
                                        <div className="header-right">
                                            <span className="voter-label">{txt.totalVoters}</span>
                                            <span className="voter-count">{address.total_voter || 0}</span>
                                        </div>
                                    </div>

                                    {/* Card Body (3 Info Boxes in One Row - Always Horizontal) */}
                                    <div className="card-body-boxes">

                                        {/* 1. Social (Green) */}
                                        <div className="info-box box-social">
                                            <div className="box-icon">
                                                <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" />
                                                </svg>
                                            </div>
                                            <div className="box-info">
                                                <span className="box-title">{txt.social}</span>
                                                <span className="box-count">{address.total_social_records || 0}</span>
                                            </div>
                                        </div>

                                        {/* 2. Facebook (Blue) */}
                                        <div className="info-box box-facebook"
                                            onClick={() => navigate('/dis_address_wise_facebook_users', { state: { address: address.add_eng } })}
                                            style={{ cursor: 'pointer' }} // માઉસ હોવર થાય ત્યારે પોઇન્ટર બતાવવા માટે
                                        >
                                            <div className="box-icon">
                                                <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z" />
                                                </svg>
                                            </div>
                                            <div className="box-info">
                                                <span className="box-title">{txt.facebook}</span>
                                                <span className="box-count">{address.total_fb_links || 0}</span>
                                            </div>
                                        </div>

                                        {/* 3. Instagram (Pink) */}
                                        <div className="info-box box-instagram"
                                            onClick={() => navigate('/dis_address_wise_insta_users', { state: { address: address.add_eng } })}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="box-icon">
                                                <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z" />
                                                </svg>
                                            </div>
                                            <div className="box-info">
                                                <span className="box-title">{txt.instagram}</span>
                                                <span className="box-count">{address.total_insta_links || 0}</span>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <style jsx>{`
                .report-page-container { width: 100%; min-height: 100vh; background: #eef2f6; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding-bottom: 40px; }
                .header-wrapper { background: white; padding: 0 24px; position: sticky; top: 0; z-index: 50; }

                /* Search Bar */
                .search-bar-container { display: flex; justify-content: flex-end; }
                .search-wrapper { position: relative; width: 100%; max-width: 400px; }
                .search-icon { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); color: #94a3b8; }
                .search-input { width: 100%; padding: 12px 40px; border: 1px solid #cbd5e1; border-radius: 12px; font-size: 15px; color: #1e293b; background: white; outline: none; transition: 0.2s; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
                .search-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
                .clear-search-btn { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); background: #f1f5f9; border: none; border-radius: 50%; padding: 4px; cursor: pointer; color: #64748b; display: flex; align-items: center; justify-content: center; transition: 0.2s; }
                .clear-search-btn:hover { background: #e2e8f0; color: #1e293b; }

                /* Spinner */
                .spinner-svg { color: #3b82f6; animation: spin 1s linear infinite; margin: 0 auto; display: block; }
                @keyframes spin { 100% { transform: rotate(360deg); } }

                /* Cards Grid - Bootstrap Grid System */
                .cards-grid {
                    margin: 0 -12px;
                }
                
                /* Address Card */
                .address-card {
                    background: #ffffff;
                    border-radius: 16px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 4px 10px rgba(0,0,0,0.03);
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                    height: 100%;
                }
                .address-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 24px -8px rgba(0,0,0,0.12);
                }

                /* Header Section (Dark Blue) */
                .card-header-bg {
                    background: #2a3763;
                    padding: 14px 16px;
                    color: white;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 12px;
                }
                .header-left {
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    flex: 1;
                    min-width: 0;
                }
                .header-icon-box {
                    background: rgba(255, 255, 255, 0.15);
                    padding: 6px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    margin-top: 2px;
                }
                .address-title {
                    margin: 0;
                    font-size: 13px;
                    font-weight: 700;
                    letter-spacing: 0.3px;
                    line-height: 1.4;
                    word-break: break-word;
                    flex: 1;
                }
                .header-right {
                    display: flex;
                    align-items: baseline;
                    gap: 6px;
                    font-size: 13px;
                    flex-shrink: 0;
                    white-space: nowrap;
                }
                .voter-label {
                    color: #cbd5e1;
                    font-weight: 600;
                }
                .voter-count {
                    font-weight: 800;
                    font-size: 16px;
                }

                /* Body Boxes (3 Info Boxes in One Row - Always Horizontal) */
                .card-body-boxes {
                    padding: 16px;
                    display: flex;
                    gap: 12px;
                    flex-direction: row;
                    justify-content: space-between;
                    flex-wrap: nowrap;
                }

                .info-box {
                    flex: 1;
                    border-radius: 10px;
                    padding: 12px 8px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    gap: 8px;
                    border: 1px solid transparent;
                    transition: transform 0.2s ease;
                    min-width: 0;
                }

                .info-box:hover {
                    transform: translateY(-2px);
                }

                /* 1. Social Box (Green) */
                .box-social {
                    background: #f0fdf4;
                    border-color: #bbf7d0;
                }
                .box-social .box-icon, .box-social .box-title, .box-social .box-count {
                    color: #16a34a;
                }

                /* 2. Facebook Box (Blue) */
                .box-facebook {
                    background: #eff6ff;
                    border-color: #bfdbfe;
                }
                .box-facebook .box-icon, .box-facebook .box-title, .box-facebook .box-count {
                    color: #2563eb;
                }

                /* 3. Instagram Box (Pink/Red) */
                .box-instagram {
                    background: #fdf2f8;
                    border-color: #fbcfe8;
                }
                .box-instagram .box-icon, .box-instagram .box-title, .box-instagram .box-count {
                    color: #db2777;
                }

                .box-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .box-icon svg {
                    width: 28px;
                    height: 28px;
                }

                .box-info {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 4px;
                    width: 100%;
                }

                .box-title {
                    font-size: 11px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    white-space: nowrap;
                }

                .box-count {
                    font-size: 22px;
                    font-weight: 900;
                    line-height: 1;
                }

                /* Responsive Breakpoints */
                @media (min-width: 1200px) {
                    .cards-grid .col-lg-4 {
                        flex: 0 0 33.333%;
                        max-width: 33.333%;
                    }
                }
                
                @media (max-width: 992px) {
                    .cards-grid .col-md-6 {
                        flex: 0 0 50%;
                        max-width: 50%;
                    }
                    .address-title {
                        font-size: 12px;
                    }
                    .voter-count {
                        font-size: 14px;
                    }
                }
                
                @media (max-width: 768px) {
                    .cards-grid .col-sm-6 {
                        flex: 0 0 50%;
                        max-width: 50%;
                    }
                    .card-body-boxes {
                        gap: 8px;
                        padding: 12px;
                    }
                    .info-box {
                        padding: 10px 6px;
                    }
                    .box-icon svg {
                        width: 24px;
                        height: 24px;
                    }
                    .box-count {
                        font-size: 18px;
                    }
                    .box-title {
                        font-size: 10px;
                        white-space: nowrap;
                    }
                    .address-title {
                        font-size: 11px;
                    }
                    .card-header-bg {
                        padding: 12px;
                    }
                }
                
                @media (max-width: 576px) {
                    .cards-grid .col-12 {
                        flex: 0 0 100%;
                        max-width: 100%;
                    }
                    .card-header-bg {
                        padding: 12px;
                        flex-wrap: wrap;
                    }
                    .header-left {
                        width: 100%;
                    }
                    .header-right {
                        margin-left: auto;
                    }
                    .card-body-boxes {
                        gap: 6px;
                        padding: 10px;
                    }
                    .info-box {
                        padding: 8px 4px;
                    }
                    .box-icon svg {
                        width: 20px;
                        height: 20px;
                    }
                    .box-count {
                        font-size: 16px;
                    }
                    .box-title {
                        font-size: 9px;
                        white-space: nowrap;
                    }
                    .address-title {
                        font-size: 11px;
                    }
                    .voter-count {
                        font-size: 14px;
                    }
                    .voter-label {
                        font-size: 11px;
                    }
                }

                /* For very small screens (320px) */
                @media (max-width: 380px) {
                    .card-body-boxes {
                        gap: 4px;
                        padding: 8px;
                    }
                    .info-box {
                        padding: 6px 3px;
                    }
                    .box-icon svg {
                        width: 18px;
                        height: 18px;
                    }
                    .box-count {
                        font-size: 14px;
                    }
                    .box-title {
                        font-size: 8px;
                    }
                    .address-title {
                        font-size: 10px;
                    }
                    .voter-count {
                        font-size: 12px;
                    }
                    .header-icon-box svg {
                        width: 14px;
                        height: 14px;
                    }
                }

                /* Animation */
                .slide-up-element { animation: slideUp 0.4s ease forwards; opacity: 0; }
                @keyframes slideUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }

            `}</style>
        </div >
    );
};

export default AddressWiseReport;